import React, { useEffect, useMemo, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { trackAnalyticsEvent } from "../services/analytics";
import { firebaseReady, observerAuth } from "../services/firebaseAuth";
import {
  loadNativeMembershipPrices,
  MembershipPlan,
  nativeBillingAvailable,
  purchaseNativeMembership,
  restoreNativeMembership,
} from "../services/subscriptions";
import { observerBackendOrigin } from "../services/runtimeConfig";

interface PlanCard {
  name: string;
  plan?: MembershipPlan;
  price: string;
  detail: string;
  features: string[];
  cta: string;
  to?: string;
}

const PricingPage: React.FC = () => {
  const [nativePrices, setNativePrices] = useState<
    Partial<Record<MembershipPlan, string>>
  >({});
  const [busy, setBusy] = useState<MembershipPlan | "restore" | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const native = nativeBillingAvailable();
  const nativePlatform = Capacitor.isNativePlatform();

  useEffect(() => {
    trackAnalyticsEvent("paywall_view", {
      source: params.get("source") || "pricing",
    });
    if (!native) return;
    const userId = firebaseReady ? observerAuth().currentUser?.uid : undefined;
    void loadNativeMembershipPrices(userId)
      .then((prices) =>
        setNativePrices({ plus: prices.plus.price, pro: prices.pro.price }),
      )
      .catch(() => {
        // The cards already carry the current storefront reference prices.
        // A simulator, offline device, or temporarily unavailable billing
        // service must not turn the whole paywall into an error state. The
        // purchase action asks the store again and reports an actionable error
        // only if the member actually tries to buy.
        trackAnalyticsEvent("paywall_price_load_failed", {
          store: Capacitor.getPlatform(),
        });
      });
  }, [native, params]);

  const plans: PlanCard[] = useMemo(
    () => [
      {
        name: "Free",
        price: "$0",
        detail: "Explore and contribute",
        features: [
          "Search every profile",
          "Read and write reviews",
          "Save up to 10 profiles",
          "Private notes up to 500 characters",
        ],
        cta: "Start exploring",
        to: "/search",
      },
      {
        name: "Plus",
        plan: "plus",
        price: nativePrices.plus || "$7.99",
        detail: "For thoughtful seekers",
        features: [
          "Unlimited saved profiles",
          "Private notes up to 2,000 characters",
          "Neutral multi-profile comparison",
          "Account-synced research Library",
        ],
        cta: "Choose Plus",
      },
      {
        name: "Pro",
        plan: "pro",
        price: nativePrices.pro || "$29.99",
        detail: "For trusted practitioners",
        features: [
          "Everything in Plus",
          "Named private research collections",
          "Private notes up to 5,000 characters",
          "Organize research by project or topic",
        ],
        cta: "Choose Pro",
      },
    ],
    [nativePrices],
  );

  const requireUser = (plan: MembershipPlan) => {
    const user = firebaseReady ? observerAuth().currentUser : null;
    if (!user) {
      const returnTo = `/pricing?plan=${plan}`;
      navigate(
        `/sign-in?plan=${plan}&returnTo=${encodeURIComponent(returnTo)}`,
      );
      return null;
    }
    return user;
  };

  const choosePlan = async (plan: MembershipPlan) => {
    const user = requireUser(plan);
    if (!user) return;
    setBusy(plan);
    setError("");
    setNotice("");
    try {
      const idToken = await user.getIdToken(true);
      if (nativePlatform) {
        if (!native)
          throw new Error(
            "Store billing has not been provisioned for this app yet.",
          );
        const result = await purchaseNativeMembership(plan, user.uid, idToken);
        setNotice(
          `${result.tier === "pro" ? "Pro" : "Plus"} is active on this account.`,
        );
      } else {
        trackAnalyticsEvent("checkout_start", {
          tier: plan,
          store: "stripe",
          product_id: `observer_${plan}_monthly`,
        });
        const response = await fetch(
          `${observerBackendOrigin}/api/observer/billing/stripe/checkout`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ plan }),
          },
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || typeof payload.url !== "string")
          throw new Error("Web checkout is temporarily unavailable.");
        window.location.assign(payload.url);
      }
    } catch (reason) {
      const detail = reason as { userCancelled?: boolean; message?: string };
      if (!detail.userCancelled)
        setError(
          detail.message || "The membership could not be activated. Try again.",
        );
    } finally {
      setBusy(null);
    }
  };

  const restore = async () => {
    const user = requireUser("plus");
    if (!user) return;
    setBusy("restore");
    setError("");
    setNotice("");
    try {
      const idToken = await user.getIdToken(true);
      const result = await restoreNativeMembership(user.uid, idToken);
      setNotice(
        result.tier === "free"
          ? "No previous membership was found for this store account."
          : `${result.tier === "pro" ? "Pro" : "Plus"} has been restored.`,
      );
    } catch (reason) {
      setError(
        (reason as { message?: string }).message ||
          "Purchases could not be restored. Try again.",
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ maxWidth: 1050, margin: "0 auto", padding: "90px 22px" }}>
      <div
        style={{ textAlign: "center", maxWidth: 700, margin: "0 auto 55px" }}
      >
        <span className="section-kicker">Simple membership</span>
        <h1
          style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "clamp(38px,6vw,66px)",
            lineHeight: 1,
            letterSpacing: "-.065em",
            margin: "15px 0",
          }}
        >
          More context.
          <br />
          Better decisions.
        </h1>
        <p style={{ color: "#66736d", lineHeight: 1.7 }}>
          The directory remains useful for free. Membership adds deeper tools
          for seekers and professionals without selling influence over ratings.
        </p>
        {notice && <p style={noticeStyle}>{notice}</p>}
        {error && <p style={{ ...noticeStyle, color: "#9f2f2f" }}>{error}</p>}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
          gap: 18,
        }}
      >
        {plans.map((plan, index) => (
          <article
            key={plan.name}
            style={{
              padding: 34,
              border: "1px solid rgba(23,35,30,.13)",
              borderRadius: 22,
              background: index === 2 ? "#17231e" : "#fffdf8",
              color: index === 2 ? "#fff" : "#17231e",
            }}
          >
            <span
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: ".14em",
                opacity: 0.65,
              }}
            >
              {plan.detail}
            </span>
            <h2
              style={{
                fontFamily: "Manrope,sans-serif",
                fontSize: 23,
                margin: "12px 0 28px",
              }}
            >
              {plan.name}
            </h2>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 4,
                fontFamily: "Manrope,sans-serif",
                fontSize: 43,
                fontWeight: 700,
                letterSpacing: "-.06em",
                lineHeight: 1.1,
              }}
            >
              {plan.price}
              <small
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  letterSpacing: 0,
                  opacity: 0.65,
                }}
              >
                /month
              </small>
            </div>
            <ul
              style={{
                minHeight: 170,
                margin: "29px 0",
                padding: 0,
                listStyle: "none",
                display: "grid",
                gap: 13,
                fontSize: 13,
                opacity: 0.8,
              }}
            >
              {plan.features.map((item) => (
                <li key={item}>✓ &nbsp;{item}</li>
              ))}
            </ul>
            {plan.to ? (
              <Link
                className="button button-ink"
                style={{ width: "100%" }}
                to={plan.to}
              >
                {plan.cta}
              </Link>
            ) : (
              <button
                type="button"
                className={`button ${index === 2 ? "button-light" : "button-ink"}`}
                style={{ width: "100%", border: "1px solid currentColor" }}
                disabled={Boolean(busy)}
                onClick={() => void choosePlan(plan.plan!)}
              >
                {busy === plan.plan ? "Opening secure checkout…" : plan.cta}
              </button>
            )}
          </article>
        ))}
      </div>
      {nativePlatform && (
        <button
          type="button"
          onClick={() => void restore()}
          disabled={Boolean(busy) || !native}
          style={restoreButton}
        >
          {busy === "restore"
            ? "Restoring…"
            : native
              ? "Restore Purchases"
              : "Store Billing Unavailable"}
        </button>
      )}
      <p
        style={{
          marginTop: 25,
          textAlign: "center",
          color: "#66736d",
          fontSize: 11,
          lineHeight: 1.7,
        }}
      >
        Subscriptions renew automatically until cancelled. Native purchases are
        billed by Apple or Google; web purchases use Stripe. Prices may vary by
        storefront and tax. <Link to="/terms">Terms</Link> ·{" "}
        <Link to="/privacy">Privacy Policy</Link>
      </p>
    </div>
  );
};

const noticeStyle: React.CSSProperties = {
  margin: "18px auto 0",
  padding: "11px 14px",
  borderRadius: 10,
  background: "#eef4ed",
  color: "#315d4d",
  fontSize: 12,
};

const restoreButton: React.CSSProperties = {
  display: "block",
  margin: "22px auto 0",
  padding: "10px 16px",
  border: "1px solid #315d4d",
  borderRadius: 9,
  background: "transparent",
  color: "#315d4d",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 700,
};

export default PricingPage;
